import { Tooltip } from '@dpem/design-system';
import { CircleHelpIcon } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { useAuth } from '@/app/providers/AuthProvider';
import { currentNavEntry, NAV_ENTRIES } from '@/router/surfaces';
import styles from './AppShell.module.css';

/**
 * The 48px icon rail: the same destinations as the header nav, reachable without
 * reading.
 *
 * Duplicating navigation is a deliberate choice, not an oversight. The header
 * nav is discoverable and the rail is fast, and on a 1280px laptop with a method
 * id and an account in the band the header nav is the first thing to run out of
 * room. Both render from `NAV_ENTRIES`, so they cannot disagree about what exists
 * or who may see it.
 *
 * Every button is a real `Link`, so middle-click and cmd-click open a tab. Icon
 * buttons carry the label in a tooltip *and* an accessible name — the tooltip is
 * for the sighted mouse user, the name is for everyone else.
 */

export function ShellRail() {
  const { capabilities } = useAuth();
  const { pathname } = useLocation();

  const entries = NAV_ENTRIES.filter((entry) => capabilities.can(entry.capability));
  const current = currentNavEntry(pathname, entries);

  return (
    <nav className={styles.rail} aria-label="Workspace" data-focus-scope="inverse">
      {entries.map((entry) => {
        const Icon = entry.icon;
        return (
          <Tooltip key={entry.id} label={entry.label} placement="right">
            <Link
              to={entry.to}
              className={styles.railButton}
              aria-label={entry.label}
              aria-current={current?.id === entry.id ? 'page' : undefined}
            >
              <Icon size={18} aria-hidden="true" />
            </Link>
          </Tooltip>
        );
      })}

      <span className={styles.railGap} />

      <Tooltip label="Keyboard shortcuts and help" placement="right">
        <a
          className={styles.railButton}
          href="https://github.com/dpem/method-studio#readme"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Keyboard shortcuts and help"
        >
          <CircleHelpIcon size={18} aria-hidden="true" />
        </a>
      </Tooltip>
    </nav>
  );
}
